import React, { createContext, memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Input, Popconfirm, Segmented, Select, Table, Tag, Tooltip, Typography } from 'antd'
import type { TableColumnsType, TablePaginationConfig } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { PaginationResponse } from '../../zod/pagination'
import { DocumentFilters } from '../api'
import { useFetchDocument, useFollowsReading, useResyncSite, useSiteDocuments } from '../hooks'
import { SiteDocument } from '../site'
import {
    CrawlRules,
    FolderCheckbox,
    PageCheckbox,
    PageMenu,
    SiteCrawlRules,
    topFolderOf,
    useCanMarkVacancies,
    useCrawlRules,
    useShowsVacancySwitch,
    VacancySwitch,
} from './site-crawl-rules'

export type SiteContentProps = {
    siteId: string
    filters: DocumentFilters
    onFiltersChange: (filters: DocumentFilters) => void
}

// A snippet website has at most 500 pages: all of them are loaded at once, so they can be grouped by folder.
const ALL_PAGES = 500
const PAGE_SIZE = 15

/** A page with what the table shows of it worked out once: its path, its title without the website name. */
type PageRow = SiteDocument & {
    path: string
    label: string
}

/** The pages in one top folder, such as "/blog/", as one row that opens. */
type FolderRow = {
    id: string
    folder: string
    vacancies: number
    skipped: number
    children: PageRow[]
}

type Row = PageRow | FolderRow

const isFolder = (row: Row): row is FolderRow => 'folder' in row
const pathOf = (row: Row) => (isFolder(row) ? row.folder : row.path)

const TITLE_SEPARATORS = [' - ', ' | ', ' – ', ' — ', ' · ']

/**
 * The website name most titles end with, such as " - Qlic Maatwerk Software Ontwikkeling", so the table shows it once
 * less per row. Null when there is no ending that at least three and a third of the titles share.
 */
function sharedTitleEnding(documents: SiteDocument[]): string | null {
    const counts = new Map<string, number>()

    for (const { title } of documents) {
        for (const separator of TITLE_SEPARATORS) {
            const index = title.lastIndexOf(separator)

            if (index > 0) {
                const ending = title.slice(index)
                counts.set(ending, (counts.get(ending) ?? 0) + 1)
            }
        }
    }

    const [ending, count] = [...counts].sort((a, b) => b[1] - a[1])[0] ?? [null, 0]
    const isShared = count >= 3 && count >= documents.length / 3

    return ending && isShared ? ending : null
}

function toPageRow(document: SiteDocument, ending: string | null): PageRow {
    const hasEnding = ending && document.title.endsWith(ending)
    const label = hasEnding ? document.title.slice(0, -ending.length) : document.title

    return {
        ...document,
        path: new URL(document.url).pathname,
        label,
    }
}

/** Folders first, then by path. */
function compareRows(a: Row, b: Row): number {
    return Number(isFolder(b)) - Number(isFolder(a)) || pathOf(a).localeCompare(pathOf(b))
}

/** Whether all pages fit in one response, so they can be shown in folders. */
function hasAllPages(response?: PaginationResponse<SiteDocument>): boolean {
    return !!response && response.meta.total <= response.data.length
}

/**
 * Pages in the same top folder as one row, its own page first; the home page and folders with one page stay single
 * rows. Folders come first, so the website reads as a few sections with the loose pages below them.
 */
function groupByFolder(documents: SiteDocument[]): Row[] {
    const ending = sharedTitleEnding(documents)
    const rows: Row[] = []
    const folders = new Map<string, PageRow[]>()

    for (const document of documents) {
        const page = toPageRow(document, ending)
        const folder = topFolderOf(document.url)

        if (folder) {
            folders.set(folder, [...(folders.get(folder) ?? []), page])
        } else {
            rows.push(page)
        }
    }

    for (const [folder, pages] of folders) {
        if (pages.length === 1) {
            rows.push(pages[0])
            continue
        }

        rows.push({
            id: `folder:${folder}`,
            folder,
            vacancies: pages.filter((page) => page.kind === 'vacancy' && page.status !== 'skipped').length,
            skipped: pages.filter((page) => page.status === 'skipped').length,
            children: pages.sort((a, b) => a.path.localeCompare(b.path)),
        })
    }

    return rows.sort(compareRows)
}

/**
 * Title and path on one line each, cut off with "…" instead of wrapping, so every row has the same height. Hidden
 * overflow also keeps both lines next to the floating space the table puts in front of a page in a folder. A skipped
 * page, or one over the page limit, is faded.
 */
const PageCell = memo(function PageCell({ page }: { page: PageRow }) {
    const { t } = useTranslation()
    const { vacancySwitch } = useRowContext()
    const isLeftOut = page.status === 'skipped' || page.status === 'limit_reached'

    return (
        <div className={`min-w-0 overflow-hidden ${isLeftOut ? 'opacity-50' : ''}`}>
            <Flex
                gap="small"
                align="center"
                className="min-w-0"
            >
                <Typography.Link
                    href={page.url}
                    target="_blank"
                    rel="noreferrer"
                    title={page.title}
                    className="truncate"
                >
                    {page.label}
                </Typography.Link>
                {/* Without the Vacancy column, vacancies are marked next to their title. */}
                {!vacancySwitch && page.kind === 'vacancy' && (
                    <Tag
                        color="blue"
                        className="m-0"
                    >
                        {t('Vacancy')}
                    </Tag>
                )}
            </Flex>
            <div
                className="truncate text-xs text-ink-tertiary"
                title={page.path}
            >
                {page.path}
            </div>
        </div>
    )
})

type RowContextValue = {
    rules: CrawlRules
    vacancySwitch: boolean
    // Without the Vacancy column, marking a page as a vacancy is in its menu, on websites Evaa reads itself.
    marksVacancies: boolean
    fetchingId?: string
    fetchPage: (documentId: string) => void
}

// What every row shares, so a row renders again when this changes, but not when another row opens or closes.
const RowContext = createContext<RowContextValue | null>(null)

function useRowContext(): RowContextValue {
    const context = useContext(RowContext)

    if (!context) {
        throw new Error('A row of the pages table is used outside it.')
    }

    return context
}

const FolderCell = memo(function FolderCell({ row }: { row: FolderRow }) {
    const { t } = useTranslation()
    const pages = row.children.length

    return (
        <div className={`min-w-0 overflow-hidden ${row.skipped === pages ? 'opacity-50' : ''}`}>
            <Flex
                gap="small"
                align="center"
                className="min-w-0"
            >
                <span className="truncate font-medium">{row.folder}</span>
                {row.vacancies > 0 && (
                    <Tag
                        color="blue"
                        className="m-0"
                    >
                        {t('{{count}} vacancies', { count: row.vacancies })}
                    </Tag>
                )}
            </Flex>
            <div className="text-xs text-ink-tertiary">
                <FolderPagesSummary row={row} />
            </div>
        </div>
    )
})

function FolderPagesSummary({ row }: { row: FolderRow }) {
    const { t } = useTranslation()
    const pages = row.children.length

    if (row.skipped === 0) {
        return t('{{count}} pages', { count: pages })
    }

    if (row.skipped === pages) {
        return t('{{count}} pages, all skipped', { count: pages })
    }

    return t('{{count}} pages, {{skipped}} skipped', {
        count: pages,
        skipped: row.skipped,
    })
}

/** Whether Evaa reads the page or the pages in the folder. */
const ReadCell = memo(function ReadCell({ row }: { row: Row }) {
    const { rules } = useRowContext()

    return isFolder(row) ? (
        // A click on the checkbox or its question does not open or close the folder, which a click on its row does.
        <span onClick={(event) => event.stopPropagation()}>
            <FolderCheckbox
                rules={rules}
                folder={row.folder}
                pages={row.children.length}
                skipped={row.skipped}
            />
        </span>
    ) : (
        <PageCheckbox
            rules={rules}
            document={row}
        />
    )
})

const VacancyCell = memo(function VacancyCell({ row }: { row: Row }) {
    const { rules } = useRowContext()

    return isFolder(row) || row.status === 'skipped' ? null : (
        <VacancySwitch
            rules={rules}
            document={row}
        />
    )
})

/** Only what needs attention: pages that are read show nothing. */
const StatusCell = memo(function StatusCell({ page }: { page: PageRow }) {
    const { t } = useTranslation()
    const tag = {
        fetched: null,
        skipped: <Tag>{t('Skipped')}</Tag>,
        limit_reached: (
            <Tooltip title={t('Your website has more pages than Evaa reads.')}>
                <Tag color="orange">{t('Over the page limit')}</Tag>
            </Tooltip>
        ),
        pending: <Tag color="blue">{t('Waiting')}</Tag>,
        failed: <Tag color="red">{t('Could not be read')}</Tag>,
        removed: <Tag>{t('Removed')}</Tag>,
        needs_javascript: (
            <Tooltip
                title={t(
                    'Evaa found no text on this page. The website probably adds it in the browser with a script, which Evaa cannot read yet. Visitors still see the page, and the chat works there. Ask your website builder to put the text in the page itself.'
                )}
            >
                <Tag color="orange">{t('No text found')}</Tag>
            </Tooltip>
        ),
    }[page.status]

    return page.error ? <Tooltip title={page.error}>{tag}</Tooltip> : tag
})

const ActionsCell = memo(function ActionsCell({ row }: { row: Row }) {
    const { rules, marksVacancies, fetchingId, fetchPage } = useRowContext()

    if (isFolder(row) || row.status === 'skipped' || row.status === 'limit_reached') {
        return null
    }

    return (
        <PageMenu
            rules={rules}
            document={row}
            withMarkAsVacancy={marksVacancies}
            reading={fetchingId === row.id}
            onReadAgain={() => fetchPage(row.id)}
        />
    )
})

export function SiteContent({ siteId, filters, onFiltersChange }: SiteContentProps) {
    const { t } = useTranslation()
    const { can } = useAuth()

    // The page number only pages through a big website; the folders show all pages at once.
    const query: DocumentFilters = {
        ...filters,
        page: undefined,
    }

    // While Evaa reads the website, the pages refresh so their status shows without refreshing the page.
    const reading = useFollowsReading(siteId)

    // All pages only refresh when they are shown in folders; otherwise only the page shown does.
    const all = useSiteDocuments(siteId, {
        filters: query,
        perPage: ALL_PAGES,
        refetchInterval: (data) => (reading && hasAllPages(data) ? 5000 : false),
    })

    // A website with more pages, such as a big WordPress website, pages through them without folders.
    const grouped = hasAllPages(all.data)

    const paged = useSiteDocuments(siteId, {
        filters,
        perPage: PAGE_SIZE,
        enabled: !!all.data && !grouped,
        refetchInterval: reading ? 5000 : false,
    })

    const queryClient = useQueryClient()
    const wasReading = useRef(reading)

    // Once more when the reading is done, for the pages read since the last refresh.
    useEffect(() => {
        if (wasReading.current && !reading) {
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'documents'] })
        }

        wasReading.current = reading
    }, [reading, queryClient, siteId])

    const fetchDocument = useFetchDocument(siteId)

    // On snippet websites: a Vacancy switch per page once there are vacancies, and before that a menu item. Every
    // website can skip pages.
    const canMarkVacancies = useCanMarkVacancies(siteId)
    const vacancySwitch = useShowsVacancySwitch(siteId) && canMarkVacancies
    const marksVacancies = canMarkVacancies && !vacancySwitch

    const rules = useCrawlRules(siteId)
    const canUpdate = can(UPDATE_SITE)
    const [search, setSearch] = useState(filters.search ?? '')
    const [expanded, setExpanded] = useState<React.Key[]>([])

    const rows = useMemo<Row[]>(() => {
        if (grouped) {
            return groupByFolder(all.data?.data ?? [])
        }

        const documents = paged.data?.data ?? []
        const ending = sharedTitleEnding(documents)

        return documents.map((document) => toPageRow(document, ending))
    }, [grouped, all.data, paged.data])

    // While searching or filtering, the folders are open, so the pages found show.
    const filtering = !!(query.search || query.status || query.kind)
    const folderKey = rows
        .filter(isFolder)
        .map((row) => row.id)
        .join()

    useEffect(() => {
        setExpanded(filtering ? folderKey.split(',').filter(Boolean) : [])
    }, [filtering, folderKey])

    useEffect(() => {
        const timer = setTimeout(() => {
            if ((filters.search ?? '') !== search) {
                onFiltersChange({
                    ...filters,
                    search: search || undefined,
                    page: 1,
                })
            }
        }, 400)

        return () => clearTimeout(timer)
    }, [search, filters, onFiltersChange])

    const fetchingId = fetchDocument.isPending ? fetchDocument.variables : undefined
    const fetchPage = fetchDocument.mutate

    const rowContext = useMemo<RowContextValue>(
        () => ({
            rules,
            vacancySwitch,
            marksVacancies,
            fetchingId,
            fetchPage,
        }),
        [rules, vacancySwitch, marksVacancies, fetchingId, fetchPage]
    )

    // Cells only render again when their own page changed; what they share comes from RowContext. Only what needs
    // attention gets a column: whether Evaa reads the page, its status when something is off, and a menu.
    const columns = useMemo<TableColumnsType<Row>>(() => {
        const rowChanged = (row: Row, previous: Row) => row !== previous

        return [
            ...(canUpdate
                ? [
                      {
                          key: 'read',
                          width: 44,
                          shouldCellUpdate: rowChanged,
                          render: (row: Row) => <ReadCell row={row} />,
                      },
                  ]
                : []),
            {
                title: t('Page'),
                key: 'title',
                shouldCellUpdate: rowChanged,
                render: (row: Row) => (isFolder(row) ? <FolderCell row={row} /> : <PageCell page={row} />),
            },
            ...(vacancySwitch
                ? [
                      {
                          title: t('Vacancy'),
                          key: 'kind',
                          width: 90,
                          shouldCellUpdate: rowChanged,
                          render: (row: Row) => <VacancyCell row={row} />,
                      },
                  ]
                : []),
            {
                title: t('Status'),
                key: 'status',
                width: 160,
                shouldCellUpdate: rowChanged,
                render: (row: Row) => (isFolder(row) ? null : <StatusCell page={row} />),
            },
            ...(canUpdate
                ? [
                      {
                          key: 'actions',
                          width: 48,
                          shouldCellUpdate: rowChanged,
                          render: (row: Row) => <ActionsCell row={row} />,
                      },
                  ]
                : []),
        ]
    }, [t, vacancySwitch, canUpdate])

    // Folders are closed, so all of them fit without paging; a big website pages through its pages.
    const pagination: TablePaginationConfig | false = grouped
        ? false
        : {
              current: paged.data?.meta.currentPage,
              total: paged.data?.meta.total,
              pageSize: paged.data?.meta.perPage,
              hideOnSinglePage: true,
              showSizeChanger: false,
              onChange: (next) => onFiltersChange({ ...filters, page: next }),
          }

    return (
        <Flex
            vertical
            gap="middle"
        >
            {canUpdate && (
                <SiteCrawlRules
                    rules={rules}
                    documents={all.data?.data ?? []}
                />
            )}
            <Flex
                gap="small"
                wrap
                justify="space-between"
            >
                <Flex
                    gap="small"
                    wrap
                >
                    <Input.Search
                        allowClear
                        placeholder={t('Search pages')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className="w-56"
                    />
                    <Segmented
                        value={filters.kind ?? 'all'}
                        onChange={(value) =>
                            onFiltersChange({
                                ...filters,
                                kind: value === 'all' ? undefined : String(value),
                                page: 1,
                            })
                        }
                        options={[
                            {
                                label: t('All'),
                                value: 'all',
                            },
                            {
                                label: t('Vacancies'),
                                value: 'vacancy',
                            },
                            {
                                label: t('Pages'),
                                value: 'content',
                            },
                        ]}
                    />
                    <Select
                        allowClear
                        placeholder={t('Status')}
                        value={filters.status}
                        onChange={(value) =>
                            onFiltersChange({
                                ...filters,
                                status: value,
                                page: 1,
                            })
                        }
                        className="w-40"
                        options={[
                            {
                                label: t('Read'),
                                value: 'fetched',
                            },
                            {
                                label: t('Waiting'),
                                value: 'pending',
                            },
                            {
                                label: t('Could not be read'),
                                value: 'failed',
                            },
                            {
                                label: t('No text found'),
                                value: 'needs_javascript',
                            },
                            {
                                label: t('Skipped'),
                                value: 'skipped',
                            },
                            {
                                label: t('Over the page limit'),
                                value: 'limit_reached',
                            },
                        ]}
                    />
                </Flex>
            </Flex>
            <RowContext.Provider value={rowContext}>
                <Table<Row>
                    dataSource={rows}
                    columns={columns}
                    rowKey="id"
                    loading={all.isLoading || paged.isLoading}
                    size="small"
                    // Fixed widths: opening a folder or another page does not move the columns.
                    tableLayout="fixed"
                    scroll={{ x: 600 }}
                    expandable={{
                        expandedRowKeys: expanded,
                        onExpandedRowsChange: (keys) => setExpanded([...keys]),
                        expandRowByClick: true,
                        // The open button and the indent of pages in a folder go next to the title, after the checkbox.
                        expandIconColumnIndex: canUpdate ? 1 : 0,
                    }}
                    pagination={pagination}
                />
            </RowContext.Provider>
        </Flex>
    )
}

type SiteResyncButtonProps = {
    siteId: string
    snippet?: boolean
}

/** Reads every page again; rarely needed, so a quiet button at the top of the card. */
export function SiteResyncButton({ siteId, snippet = false }: SiteResyncButtonProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const resync = useResyncSite(siteId)

    if (!can(UPDATE_SITE)) {
        return null
    }

    return (
        <Popconfirm
            title={t('Read all pages again?')}
            description={
                snippet
                    ? t('Evaa reads the website again and finds new pages. This takes a few minutes.')
                    : t('Evaa reads every page again in the background. This takes a few minutes.')
            }
            onConfirm={() => resync.mutate()}
            okText={t('Read again')}
            cancelText={t('Cancel')}
            placement="bottomRight"
        >
            <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                loading={resync.isPending}
            >
                {t('Read all pages again')}
            </Button>
        </Popconfirm>
    )
}
